/**
 * **O que as cartas desenham na arena** (gate V28H.L).
 *
 * Três coisas que não existiam: a lâmina do Turbilhão a girar à volta de um
 * casco, o feixe da Lança Solar a arder, e o anel de uma carga.
 *
 * ## O que este ficheiro NÃO decide
 *
 * Nada. Ele recebe FACTOS - a sala disse que esta carta está a desenhar isto -
 * e devolve geometria. A lâmina não causa dano, o feixe não escolhe alvo, e a
 * carga não decide quando acaba. Tudo isso é da simulação, e tem de ser: um
 * cliente que decidisse o comprimento do feixe desenharia uma linha diferente
 * da que fere.
 *
 * ## Porque a geometria vive aqui e não na cena
 *
 * Porque ela é a MESMA que a simulação usa, e escrita em proporções do casco
 * exactamente como lá. Uma segunda expressão na cena seria a maneira habitual
 * de um feixe passar a ser desenhado ao lado do sítio onde ele cobra.
 */

/**
 * Uma lista de letras vinda do fio, lida como três perguntas.
 *
 * Uma função e não três comparações espalhadas: quem acrescentar uma quarta
 * letra escreve-a aqui, e a cena pergunta pelo nome em vez de por um caractere.
 */
export interface CardVisualFlags {
  /**
   * O círculo mágico do Turbilhão, que é PASSIVO (gate V28H.O).
   *
   * Verdadeiro pela carta estar EQUIPADA, e não por ela estar ligada. É a única
   * destas quatro que não descreve um acontecimento.
   */
  readonly circle: boolean;
  /** E a serra a cortar, que é o acontecimento. */
  readonly blade: boolean;
  readonly beam: boolean;
  /**
   * A LANÇA a carregar, e já não "alguma carta" (gate V28H.U).
   *
   * Ela e a Esfera desenham coisas diferentes - uma corona no bico, uma bola que
   * incha à frente do casco - e enquanto isto foi uma pergunta só, a cena
   * desenhava a corona por cima de uma esfera que ninguém via.
   */
  readonly charging: boolean;
  /** E a ESFERA a carregar, que é a outra metade da mesma pergunta. */
  readonly sphere: boolean;
  /**
   * **As três cartas de movimento** (gate V28H.V).
   *
   * `dash` é o Arranque a avançar - 250 ms - e `haste` é a Sobremarcha ligada.
   * As duas desenham atrás do casco, e nunca à frente dele.
   *
   * `cloaked` é o Manto, e é a única destas perguntas cuja resposta um
   * adversário nunca vê: um casco encoberto é OMITIDO do frame de quem não é da
   * equipa dele, portanto esta letra só chega a quem tem direito a desenhar a
   * silhueta - o dono, e os aliados.
   */
  readonly dash: boolean;
  readonly haste: boolean;
  readonly cloaked: boolean;
  /** O Malware acabou de sair DESTE casco (gate V28H.W): o hack, e não a infecção. */
  readonly malwareCast: boolean;
  /** E a Marca da Caveira, pela mesma razão (gate V28H.X). */
  readonly markCast: boolean;
}

export function cardVisualFlags(
  visuals: readonly ('o' | 'b' | 'l' | 'c' | 'g' | 'd' | 'h' | 'k' | 'w' | 'm')[] | undefined,
): CardVisualFlags {
  const list = visuals ?? [];
  return {
    circle: list.includes('o'),
    blade: list.includes('b'),
    beam: list.includes('l'),
    charging: list.includes('c'),
    sphere: list.includes('g'),
    dash: list.includes('d'),
    haste: list.includes('h'),
    cloaked: list.includes('k'),
    malwareCast: list.includes('w'),
    markCast: list.includes('m'),
  };
}

/**
 * **O raio a que a lâmina do Turbilhão orbita**, em unidades de mundo.
 *
 * A mesma proporção que a simulação usa para decidir quem ela apanha - o
 * alcance é basis points do raio do casco - portanto a lâmina é desenhada
 * exactamente onde ela fere. Um número escolhido aqui seria uma lâmina que
 * parece apanhar quem não apanha.
 */
export function bladeOrbitRadius(hullDiameter: number, reachBps: number): number {
  /* O diâmetro desenhado é dois raios: a proporção é sobre o RAIO. */
  return ((hullDiameter / 2) * reachBps) / 10_000;
}

/**
 * O ângulo da lâmina neste instante, em radianos.
 *
 * Derivado do RELÓGIO da cena e não de um tique da sala: a rotação é decoração
 * pura - a simulação apanha toda a gente que está ao alcance, seja qual for o
 * ângulo - e duas abas com fases diferentes vêem o mesmo jogo.
 */
export function bladeAngle(elapsedMs: number, periodMs: number): number {
  return ((elapsedMs % periodMs) / periodMs) * Math.PI * 2;
}

/** Uma volta completa por segundo. Rápida o suficiente para se ler como giro. */
export const BLADE_PERIOD_MS = 1_000;

/**
 * **O comprimento e a espessura do feixe**, em unidades de mundo.
 *
 * As duas proporções que a simulação usa, e pela mesma razão que a lâmina: o
 * feixe é desenhado exactamente onde ele cobra. A espessura devolvida é a
 * TOTAL - o dobro da meia-espessura que a simulação compara.
 */
export function beamGeometry(
  hullDiameter: number,
  reachBps: number,
  widthBps: number,
): { readonly length: number; readonly width: number } {
  const radius = hullDiameter / 2;
  return {
    length: (radius * reachBps) / 10_000,
    width: ((radius * widthBps) / 10_000) * 2,
  };
}

/**
 * O tamanho desenhado de uma invocação, em unidades de mundo.
 *
 * **A escala do Leviatã sai da vida da nave que o criou, com um mínimo grande.**
 * É uma decisão aprovada, e a metade que o cliente consegue cumprir é esta: a
 * sala manda a vida dele em proporção da máxima, e o tamanho vem do casco do
 * dono - que é a mesma curva que desenha o casco.
 *
 * O mínimo existe porque um Leviatã pequeno não é um Leviatã: uma nave tem de
 * caber lá dentro para a entrada física fazer sentido.
 */
export function summonDiameter(
  kind: 'd' | 'l',
  ownerHullDiameter: number,
  minimumLeviathan: number,
): number {
  if (kind === 'd') {
    return ownerHullDiameter * DRONE_SIZE_FACTOR;
  }
  return Math.max(minimumLeviathan, ownerHullDiameter * LEVIATHAN_SIZE_FACTOR);
}

/**
 * **Onde e quão grande está a esfera de um casco a CARREGAR** (gate V28I.P.1).
 *
 * ## Porque isto é aritmética e não desenho
 *
 * O raio sai do MESMO caminho que a sala percorre quando a tecla se solta — o
 * alcance em basis points do raio do casco, graduado pela força já cumprida — e
 * a fração do corpo é a mesma que qualquer arena desenha. O que se vê é o que
 * sai, e um número escolhido do lado do desenho seria uma promessa que a sala
 * não paga.
 *
 * ## Porque saiu de dentro do duelo
 *
 * Porque passou a ser preciso em dois sítios. Ela era um método privado da cena
 * do duelo; o mundo aberto carrega a mesma esfera, e uma segunda cópia da mesma
 * curva seria a segunda oportunidade de a bola mentir num dos modos.
 *
 * O alcance fica de FORA de propósito: ele é uma medida de arena, e cada uma tem
 * o seu — foi exatamente por isso que ele saiu do catálogo.
 *
 * Nulo quando ninguém está a carregar, que é o estado de quase todo o casco.
 */
export function chargingSphereGeometry(input: {
  /** A fração JÁ CUMPRIDA da carga, 0 a 100, tal como a sala a publica. */
  readonly chargePercent: number | undefined;
  readonly x: number;
  readonly y: number;
  readonly heading: number;
  readonly hullRadius: number;
  /** O alcance desta arena, em basis points do raio do casco. */
  readonly reachBps: number;
  /** A curva canónica da sala. Injetada para este módulo não importar o domínio. */
  readonly radiusFor: (fullRadius: number, strengthBps: number) => number;
  /** Que fração do raio da explosão é o CORPO desenhado. */
  readonly bodyFraction: number;
}): { readonly x: number; readonly y: number; readonly radius: number } | null {
  if (input.chargePercent === undefined) {
    return null;
  }
  /*
   * A fração CRUA, e não o chão da carga mínima: o que a sala publica é quanto
   * da carga já foi cumprido. Com o chão por cima, a bola nascia a meio do
   * tamanho final e ficava lá durante o primeiro quarto da carga. O desenho
   * nunca promete mais do que aquilo que sai; no primeiro quarto promete um
   * pouco menos, que é a escolha certa das duas.
   */
  const strengthBps = (input.chargePercent * 10_000) / 100;
  const blastRadius = input.radiusFor((input.hullRadius * input.reachBps) / 10_000, strengthBps);
  const radius = blastRadius * input.bodyFraction;
  /*
   * À FRENTE da boca, e nunca dentro do casco: ela nasce onde vai sair, e o meio
   * raio de folga é o que impede uma bola a crescer de comer a nave que a está
   * a segurar.
   */
  const distance = input.hullRadius + radius * 0.5;
  return {
    x: input.x + Math.cos(input.heading) * distance,
    y: input.y + Math.sin(input.heading) * distance,
    radius,
  };
}
/** Um drone é menor do que a nave que o largou: ele é um acessório dela. */
export const DRONE_SIZE_FACTOR = 0.7;

/** E um Leviatã é maior: uma nave inteira entra lá dentro. */
export const LEVIATHAN_SIZE_FACTOR = 3.5;

/**
 * O mínimo de um Leviatã, em cascos de referência.
 *
 * **"Tamanho mínimo grande" é uma decisão aprovada**, e isto é a metade que o
 * cliente cumpre: um Leviatã pequeno não é um Leviatã, porque uma nave tem de
 * caber lá dentro para a entrada física fazer sentido.
 */
export const LEVIATHAN_MIN_HULLS = 4;

/* ------------------------------------------------------------------------ */
/*  O BASTIÃO: onde está cada estrela, e de que cor (gate V28H.U).           */
/*                                                                          */
/*  Aqui e não ao lado do desenho, pela razão que este ficheiro já tem: a    */
/*  aritmética é provável sem um runtime WebGL, e a arte que a usa importa   */
/*  Phaser. Um teste que precisasse de um contexto de canvas para verificar  */
/*  que uma estrela não entra no casco não seria escrito - e a promessa      */
/*  ficaria por provar.                                                     */
/* ------------------------------------------------------------------------ */

/** Quantas estrelas orbitam um casco ao mesmo tempo. */
const STARS = 9;

/** Quanto dura a vida de uma estrela, do nascimento ao desaparecimento. */
const STAR_LIFE_MS = 1_150;

/** Que fracção dessa vida ela passa em órbita antes de se destacar. */
const ORBIT_FRACTION = 0.55;

/**
 * **As três cores, e a ordem delas.**
 *
 * Branco, dourado e ciano néon - as que o pedido nomeia. Elas rodam por estrela
 * e devagar: a alternância é o efeito, e uma troca depressa seria a cintilação
 * agressiva que o mesmo pedido proíbe.
 */
const COLOURS = [0xff_ff_ff, 0xff_d9_6a, 0x7f_e9_ff] as const;

/** De quanto em quanto tempo a roda de cores avança um passo. */
const COLOUR_STEP_MS = 900;

/** A cor desta estrela agora: a roda das três, um passo de cada vez. */
export function bastionColourAt(index: number, nowMs: number): number {
  const step = Math.floor(nowMs / COLOUR_STEP_MS);
  return COLOURS[(index + step) % COLOURS.length] ?? COLOURS[0];
}

/**
 * Onde está uma estrela, e como está, no instante `nowMs`.
 *
 * Exportada porque é ARITMÉTICA e não desenho: é ela que garante que a estrela
 * orbita antes de se destacar, que nunca entra no casco e que se apaga a
 * afastar-se. Prova-se sem um renderizador.
 */
export function bastionStarAt(
  index: number,
  nowMs: number,
  hullRadius: number,
): { x: number; y: number; scale: number; alpha: number; colour: number } {
  const phase = (((nowMs / STAR_LIFE_MS + index / STARS) % 1) + 1) % 1;
  /*
   * O ângulo de nascimento espalha-se pelo índice com um passo irracional: um
   * passo de `2*pi/STARS` daria nove posições fixas, e as estrelas nasceriam
   * sempre nos mesmos nove sítios - lê-se como um mecanismo, e não como faíscas.
   */
  const base = index * 2.399_963 + Math.floor(nowMs / STAR_LIFE_MS + index / STARS) * 0.7;
  if (phase < ORBIT_FRACTION) {
    const orbit = phase / ORBIT_FRACTION;
    const angle = base + orbit * 1.5;
    const radius = hullRadius * 0.82;
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      /* Cresce ao nascer: uma estrela que aparecesse no tamanho final pisca. */
      scale: 0.45 + 0.55 * Math.min(1, orbit * 3),
      alpha: Math.min(1, orbit * 4),
      colour: bastionColourAt(index, nowMs),
    };
  }
  /* Destacada: continua no ângulo em que largou, afasta-se e apaga-se. */
  const away = (phase - ORBIT_FRACTION) / (1 - ORBIT_FRACTION);
  const angle = base + 1.5;
  const radius = hullRadius * (0.82 + 0.75 * away);
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    scale: 1 - 0.5 * away,
    alpha: 1 - away ** 1.5,
    colour: bastionColourAt(index, nowMs),
  };
}

/** Quantas estrelas um casco protegido tem ao mesmo tempo. */
export const BASTION_STAR_COUNT = STARS;
/** Quanto dura cada uma, do nascimento ao fim. */
export const BASTION_STAR_LIFE_MS = STAR_LIFE_MS;
/** A fracção da vida passada em órbita, antes de se destacar. */
export const BASTION_ORBIT_FRACTION = ORBIT_FRACTION;
/** As três cores da alternância, na ordem em que rodam. */
export const BASTION_COLOURS = COLOURS;
